import React, { useState } from 'react';
import styled from 'styled-components';
import { useScreenClass } from 'react-grid-system';

const Page = styled.div`
  background: #f9f9f9;
  padding: 1rem 1rem 6rem 1rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Layout = styled.div<{ isDesktop: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.isDesktop ? '1fr 1fr' : '1fr'};
  gap: ${(props) => (props.isDesktop ? '8rem' : '1rem')};
  align-items: start;
`;

const Title = styled.h1<{ isDesktop: boolean }>`
  font-size: ${(props) => (props.isDesktop ? '55px' : '42px')};
  text-align: center;
  margin: ${(props) => (props.isDesktop ? '6rem 0 3rem 0' : '3rem 0 1.5rem 0')};
  color: #000;
`;

const Subtitle = styled.p<{ isDesktop: boolean }>`
  font-size: ${(props) => (props.isDesktop ? '1.25rem' : '1rem')};
  text-align: center;
  margin: ${(props) => (props.isDesktop ? '0.5rem 0 2rem 0' : '0.5rem 0 1rem 0')};
  color: #555;
`;

const SectionHeading = styled.h2`
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  color: #000;
  font-size: 1.75rem;
`;

const Item = styled.div`
  border-bottom: 1px solid #ddd;
  padding-bottom: 0.5rem;
`;

const Question = styled.button<{ isOpen: boolean }>`
  width: 100%;
  background: none;
  border: none;
  padding: 0.75rem 0;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.darkRed};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  &::after {
    content: '${(p) => (p.isOpen ? '−' : '+')}';
    font-size: 1.5rem;
  }
`;

const Answer = styled.div<{ isOpen: boolean }>`
  max-height: ${(p) => (p.isOpen ? '999px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-bottom: ${(p) => (p.isOpen ? '1rem' : '0')};
  p {
    margin: 0.5rem 0;
    line-height: 1.6;
  }
`;

const Subheading = styled.h3`
  font-size: 1.25rem;
  margin: 1rem 0 0.5rem;
  font-weight: 600;
`;

// Grouped FAQ sections
const faqSections = [
  {
    section: 'Program Basics',
    items: [
      { question: 'How long is the program?', answer: 'Two semesters, September through May. We meet weekly on Tuesday evenings from 6:00 to 9:00 PM ET.' },
      { question: 'Can I participate remotely?', answer: 'Yes! Although our classroom is located in NYC, you can join us virtually from anywhere. We have participants from around the world. We record all classes, and you can watch or rewatch them at your convenience.' },
      { question: 'There are quite a few faculty members. What do they each do?', answer: `Great question, glad you noticed!  
Two primary instructors teach every class and hold regular individual meetings with students: Kerry Donahue and Chris O'Keeffe serve as co-instructors for the 2025-2026 academic year.  
Three specialist instructors join for specific courses: Zach McNees (audio engineering and sound design), Amanda McLoughlin (marketing, monetization and strategy), and Catherine Burns (storytelling and narrative craft).  
Program leadership: Kathleen Russo, the program director and co-founder, reviews applications, selects students, manages scholarships, and serves as your initial contact. Imran Azad, a program alum, handles marketing and application logistics.` },
      { question: 'What’s the time commitment?', answer: 'Plan for three hours of class time weekly, plus time for assignments, recording, and production. This class is for you, and we encourage you to keep coming to class even when life gets too busy to complete the assignments. That said, like most things, you will get out as much as you put into it.' },
      { question: 'Will podcasting survive the current market difficulties?', answer: 'Absolutely! This relatively young industry is changing rapidly - more podcasts are now both audio and video, companies are consolidating - but as long as there are times when people can listen while doing something else, podcasts will have a place in many people\'s lives. Plus, the skills you learn in making, marketing and monetizing podcasts are transferable to other kinds of content creation.' },
    ],
  },
  {
    section: 'Application & Admissions',
    items: [
      { question: 'When is the application deadline?', answer: '11:59 pm ET, Friday, August 15, 2025.' },
      { question: 'Do I need to have a podcast already?', answer: 'No. We work with people who have great ideas but haven\'t launched yet, as well as those who want to grow or revive existing shows.' },
      { question: 'What makes a strong application?', answer: 'We\'re seeking compelling ideas, clear target audiences, and a genuine commitment to the process. Previous podcasting experience isn\'t required.' },
      { question: 'How many people are accepted?', answer: 'We keep cohorts small (12-15 people) to ensure personalized attention and foster a strong sense of community.' },
      { question: 'If I don’t have a podcast yet, do I need to submit an audio sample?', answer: 'What we want is to get a sense of your tone or idea for the podcast. You can record a two-minute pitch to a potential listener explaining why they should listen to your podcast.' },
      { question: 'What kind of podcast are you looking for?', answer: 'We encourage all types of podcasts!' },
      { question: 'Am I too old/young or too experienced/inexperienced?', answer: 'Almost certainly not. We have had students of all ages, from college age to their 80s, including individuals who have never touched a microphone and those who have worked in audio storytelling for years.' },
      { question: 'What if I change my mind about the podcast in my application and want to produce something different?', answer: 'No problem! It’s normal for ideas to evolve and change, sometimes in significant ways, throughout the program.' },
      { question: 'Is there an application fee?', answer: 'There is no fee to apply. Go for it!' },
    ],
  },
  {
    section: 'Costs & Support',
    items: [
      { question: 'What does the program cost?', answer: '$8,200' },
      { question: 'Do you offer financial aid?', answer: 'Since this is a non-degree course, students are not eligible for financial aid; however, scholarships may be available on a case-by-case basis. To be considered for a scholarship or work study, apply first, and we\'ll schedule a conversation about support options. All students are encouraged to explore Stony Brook’s Time Option Payment Plan (TOPP).' },
      { question: 'What’s included in tuition?', answer: 'In addition to the classes, guest lectures, and mentorship, you will receive some equipment and some software (thanks to our sponsors Samson and Descript), and access to the discounts available with an .edu address.' },
    ],
  },
  {
    section: 'About Stony Brook University',
    items: [
      { question: 'What is Stony Brook University?', answer: 'Stony Brook University is a top-tier public research university with 17,000 students and a main campus on Long Island\'s North Shore. The Podcast Incubator is part of the Lichtenstein Center at Stony Brook University, which offers academic programs in creative writing, film, television writing, and podcasting.' },
      { question: 'Will I receive a degree? How about grades?', answer: 'No and no. The Podcast Incubator is a non-graded, non-degree-granting program. But, at the end, you\'ll have a polished pilot, a clear game plan, and industry connections - the real credentials that matter in this field. Plus, you’ll receive a badge for your LinkedIn page and a career\'s worth of people rooting for you.' },
    ],
  },
  {
    section: 'Technical & Production',
    items: [
      { question: 'Do I need my own equipment?', answer: 'No. We provide studio access, but, like many professional podcasters, students often prefer to build their own basic home setup. We\'ll guide you on what you actually need vs. what\'s nice to have.' },
      { question: 'What if I’m not tech-savvy?', answer: 'While it helps to be comfortable using computers, we teach technical skills related to audio production. You don\'t need to arrive knowing how to edit audio - that\'s what we\'re here for.' },
      { question: 'Will I launch my podcast during the program?', answer: 'Yes, you\'ll complete and launch a market-ready pilot or first episodes during the second semester with our guidance.' },
    ],
  },
  {
    section: 'Career & Outcomes',
    items: [
      { question: 'Where do graduates get jobs?', answer: 'Many alumni continue to work on their podcasts as independent projects, while others secure internships or jobs at established podcast companies, including Spotify, iHeartRadio, and WNYC. We are committed to helping you develop transferable skills that make you a competitive candidate for podcasting jobs and other types of content creation.' },
      { question: 'Is this program right for someone who wants to podcast as a hobby?', answer: 'Our program is designed for people who want to create professional-quality work that finds and serves a real audience, whether as a career move or serious creative pursuit.' },
    ],
  },
];

const FAQPage: React.FC = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<string[]>(['sec-0']);
  const toggleSection = (id: string) => {
    setOpenSections((prev) =>
      prev.includes(id) ? [] : [id]
    );
  };
  const screenClass = useScreenClass();
  const isDesktop = ['lg', 'xl'].includes(screenClass);

  return (
    <Page>
      <Container>
        <Title isDesktop={isDesktop}>Frequently Asked Questions</Title>
        <Subtitle isDesktop={isDesktop}>Click a topic below to expand and view details</Subtitle>
        <Layout isDesktop={isDesktop}>
          <div>
            {faqSections.slice(0, 3).map((sec, sidx) => {
              const sectionId = `sec-${sidx}`;
              const isSectionOpen = openSections.includes(sectionId);
              return (
                <React.Fragment key={sidx}>
                  <SectionHeading
                    onClick={() => toggleSection(sectionId)}
                    style={{ cursor: 'pointer' }}
                  >
                    {sec.section} {isSectionOpen ? '−' : '+'}
                  </SectionHeading>
                  {isSectionOpen &&
                    sec.items.map((faq, idx) => {
                      const id = `${sidx}-${idx}`;
                      const isOpen = openId === id;
                      return (
                        <Item key={id}>
                          <Question isOpen={isOpen} onClick={() => setOpenId(isOpen ? null : id)}>
                            {faq.question}
                          </Question>
                          <Answer isOpen={isOpen}>
                            {faq.question === 'There are quite a few faculty members. What do they each do?' ? (
                              <>
                                <p>Great question, glad you noticed! We're proud of our low student-to-faculty ratio and the complementary strengths of our highly experienced team.</p>
                                <Subheading>Primary Instructors</Subheading>
                                <p>
                                  Two primary instructors teach every class and hold regular individual meetings with students: <strong>Kerry Donahue</strong> and <strong>Chris O'Keeffe</strong> serve as co-instructors for the 2025-2026 academic year.
                                </p>
                                <Subheading>Specialist Instructors</Subheading>
                                <p>
                                  Three specialist instructors join for specific courses: <strong>Zach McNees</strong> (audio engineering and sound design), <strong>Amanda McLoughlin</strong> (marketing, monetization and strategy), and <strong>Catherine Burns</strong> (storytelling and narrative craft).
                                </p>
                                <Subheading>Program Leadership</Subheading>
                                <p>
                                  <strong>Kathleen Russo</strong>, the program director and co-founder, reviews applications, selects students, manages scholarships, and serves as your initial contact. <strong>Imran Azad</strong>, a program alum, handles marketing and application logistics.
                                </p>
                              </>
                            ) : (
                              faq.answer.split('\n').map((line, i) => <p key={i}>{line}</p>)
                            )}
                          </Answer>
                        </Item>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </div>
          <div>
            {faqSections.slice(3).map((sec, sidx) => {
              const keyId = `col2-${sidx}`;
              const sectionId = `sec-${sidx + 3}`;
              const isSectionOpen = openSections.includes(sectionId);
              return (
                <React.Fragment key={keyId}>
                  <SectionHeading
                    onClick={() => toggleSection(sectionId)}
                    style={{ cursor: 'pointer' }}
                  >
                    {sec.section} {isSectionOpen ? '−' : '+'}
                  </SectionHeading>
                  {isSectionOpen &&
                    sec.items.map((faq, idx) => {
                      const id = `${sidx + 3}-${idx}`;
                      const isOpen = openId === id;
                      return (
                        <Item key={id}>
                          <Question isOpen={isOpen} onClick={() => setOpenId(isOpen ? null : id)}>
                            {faq.question}
                          </Question>
                          <Answer isOpen={isOpen}>
                            <p>{faq.answer}</p>
                          </Answer>
                        </Item>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </div>
        </Layout>
      </Container>
    </Page>
  );
}

export default FAQPage;